import {Game} from "../Game";
import {GameAssets} from "../GameAssets";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {IMonsterWalk} from "../server/IData";
import {Sound} from "../sound/Sound";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";

export enum SymbolStates {
    NORMAL = "base",
    WIN = "win",
    SPLIT = "split",
    LAND = "land",
    UPGRADE = "upgrade",
    SPLIT_UPGRADE = "splitupgrade",
    IDLE = "idle",
}

export enum XSplitStates {
    DISAPPEAR = "disappear",
    TRIGGER = "trigger"
}

export enum WildStates {
    DARK = "dark",
    SPLIT_DARK = "splitdark",
    SPLIT_BASE = "splitbase",
    NUDGE_LAND = "nudge_land"
}

export enum XBombStates {
    EXPLODE = "explode_ver",
    TRIGGER = "trigger"
}

export enum XNudgeStates {
    NUDGE = "nudge",
    LAST_NUDGE = "last_nudge",
    CAN_NOT_MOVE = "cant_move"
}

export enum GEStates {
    TRIGGER_HOR = "trigger_hor",
    TRIGGER_VER = "trigger_ver",
    TRIGGER_MAX = "trigger_max"
}

export class Symbol extends PIXI.Container {
    public animation: PIXI.spine.Spine;
    public aliasName: string; // #248
    protected _isMonster: boolean;
    protected _symbolSprite: PIXI.Sprite;
    private readonly _isPayoutSymbol: boolean;
    private _highlight: PIXI.Sprite;
    private _mXAnimation: PIXI.spine.Spine;
    private _isDark: boolean; // used only with W, #367
    private _isAnimatingWithoutTl: boolean;
    private _isSplit: boolean;
    private _wildSkinName: string;
    private _isRemoved: boolean;
    private _reelPosition: number;

    // reelPosition here refers to rowPosition
    constructor(symbolName: string, initState: SymbolStates, reelPosition: number, trackIdx: number = 0, loop: boolean = false) {
        super();
        this.name = symbolName;
        this.aliasName = symbolName;

        if (["CR", "AV", "MX", "L"].includes(symbolName)) {
            this.visible = false;
        }

        this.setAnimationOrSprite();
        this.createHighlightItem();
        this.addChildAt(this._highlight, 0);

        this._reelPosition = reelPosition;

        this._isRemoved = false;

        this._isPayoutSymbol = (Game.PAYOUT_SYMBOLS.includes(symbolName));
        this._isMonster = false;
        this._isSplit = false;
        this._isAnimatingWithoutTl = false;
        this._isDark = false;

        if (symbolName.includes("MX_V")) {
            Game.mxSymbol = this;

            Game.shakeScreen();
        }

        this.getStateAnimationWithoutTL(initState, trackIdx, loop);
    }

    public splitSymbol(): gsap.core.Timeline {
        if (this.name !== "XS" && this.name !== "XN") {
            this._isSplit = true;
        }

        if ((Game.ORDINARY_PAYOUT_SYMBOLS.includes(this.name)) || (this.name === "W") || (!this.name.includes("MX") && this.name.charAt(0) === 'M')) {
            return this.getStateAnimation(SymbolStates.SPLIT).eventCallback('onComplete', () => {
                if (!this.name.includes("MX") && this.name.charAt(0) === 'M') {
                    const multiplierVal: number = Number(this.name.slice(1));

                    this.aliasName = 'M' + (multiplierVal * 2);
                    this.changeSymbolSkin(this.getMultiplierSkinLevel());
                }
            });
        } else {
            return gsap.timeline();
        }
    }

    public updateTexture(newName: string): void {
        this.name = newName;
        this.aliasName = newName;

        this.setAnimationOrSprite();
    }

    public showHighlight(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._highlight.visible || !Game.ORDINARY_PAYOUT_SYMBOLS.includes(this.name)) {
            return tl;
        }

        this._highlight.alpha = 0;
        this._highlight.visible = true;

        tl.to(this._highlight, {
            alpha: 1,
            duration: 0.2
        }, 0);

        return tl;
    }

    public updateHighlight(idx: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._highlight.visible) {
            return tl;
        }

        const pSymName: string = Game.ORDINARY_PAYOUT_SYMBOLS[idx];

        this._highlight.texture = Game.assetLoader.getTextures(GameAssets.highlightPaySymbolMap.get(pSymName) || "HL_C4")[0];
        this._highlight.alpha = 0;
        this._highlight.visible = true;

        tl.to(this._highlight, {
            alpha: 1,
            duration: 0.2
        }, 0);

        return tl;
    }

    public removeHighlight(): void {
        if (!this._highlight.visible) {
            return;
        }

        this._highlight.visible = false;
    }

    public getStateAnimationWithoutTL(state: string, trackIndex: number = 0, loop: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (this.animation && this.animation.state.hasAnimation(state)) {
            const track: PIXI.spine.core.TrackEntry = this.animation.state.setAnimation(trackIndex, state, loop);
            this._isAnimatingWithoutTl = true;

            tl.add(() => {
                this.animation.autoUpdate = true;
            });
            tl.to(this, {
                duration: track.animation.duration
            });
            tl.add(() => {
                this._isAnimatingWithoutTl = false;
            });
        }
        return tl;
    }

    public getStateAnimationTL(state: string, trackIndex: number = 0, loop: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (this.animation && this.animation.state.hasAnimation(state)) {
            tl.add(Game.createTimelineForAnimation(this.animation, trackIndex, state, loop));// this.animation.state.setAnimation(trackIndex, state, loop);
        }
        return tl;
    }

    public getStateAnimation(state: string, trackIndex: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this.animation) {
            tl.add([
                () => {
                    this.animation.autoUpdate = false;
                },
                Game.createTimelineForAnimation(this.animation, trackIndex, state)
            ], 0);
        }

        return tl;
    }

    public getMultiplierSkinLevel(): string {
        let multiplierSym: string = this.aliasName.toLowerCase();

        let multiplierLvl: string = Game.getMultiplierLvl(multiplierSym);

        return multiplierLvl + "/" + multiplierSym;
    }

    public changeSymbolSkin(skinName: string): void {
        if (this.animation) {
            this.animation.autoUpdate = true;
            this.animation.skeleton.setSkinByName(skinName);

            if (this.name === "W") {
                this._wildSkinName = skinName;
            }
        }
    }

    public playExplodeEffect(isKillCount: boolean): gsap.core.Timeline {
        const item: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("single_explos/single_explos_")),
            tl: gsap.core.Timeline = gsap.timeline();

        item.anchor.set(0.5);
        item.blendMode = PIXI.BLEND_MODES.SCREEN;
        item.scale.set(Game.scaleFactor);
        item.hide();
        this.addChild(item);

        if (this.animation) {
            tl.to(this.animation.scale, {
                duration: 0.5,
                x: 0,
                y: 0
            });
        } else {
            tl.to(this._symbolSprite.scale, {
                duration: 0.5,
                x: 0,
                y: 0
            });
        }
        if (isKillCount) {
            tl.add(() => {
                Game.overlaysForSymbols.playBzBreak(this);
            }, "<");
            tl.to(this, {
                duration: 20 / 30 // number of frames in above anim
            }, "<");
        } else {
            tl.add(<gsap.core.Timeline><unknown>item.getAnimationAutoShowHide(true, true), "<");
        }

        tl.add(() => {
            if (item.parent) {
                item.parent.removeChild(item);
                item.destroy();
            }

            if (this.parent) {
                this.parent.removeChild(this);
                if (this.animation) {
                    this.animation.scale.set(1);
                } else {
                    this._symbolSprite.scale.set(1);
                }
            }
        });

        return tl;
    }

    public playBZBigExplode(monsterSym: Symbol, scale: number = 1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // #400
        if (!monsterSym) return tl;

        const bigExplodeSpot: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("fx_big_explode/big_explode_0"));
        bigExplodeSpot.blendMode = PIXI.BLEND_MODES.SCREEN;
        bigExplodeSpot.alpha = 0;
        bigExplodeSpot.name = "bigExplodeSpot" + monsterSym.name;

        bigExplodeSpot.anchor.set(0.5);
        bigExplodeSpot.scale.set(scale);

        tl.add(() => {
            // bigExplodeSpot.position.set(monsterSym.position.x, monsterSym.position.y);
        });
        this.addChild(bigExplodeSpot);
        tl.to(bigExplodeSpot, {
            duration: 0.01,
            alpha: 1
        });
        tl.add(<gsap.core.Timeline><unknown>bigExplodeSpot.getAnimationAutoShowHide(true, false));
        tl.add(() => {
            bigExplodeSpot?.parent?.removeChild(bigExplodeSpot);
        });
        return tl;
    }

    public playXBombExplodeAnimation(animName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("XB"));

        item.scale.set(Game.scaleFactor);

        tl.add(() => {
            this.addChild(item);
            if (animName === XBombStates.EXPLODE) {
                this.visible = false;
            }
        });
        tl.add(Game.createTimelineForAnimation(item, 0, animName));
        tl.add(() => {
            if (item.parent) {
                item.parent.removeChild(item);

                item.destroy();
            }
        });

        return tl;
    }

    public playBigExplode(scale : number = 1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        const bigExplodeSpot: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("fx_big_explode/big_explode_0"));
        bigExplodeSpot.blendMode = PIXI.BLEND_MODES.SCREEN;
        bigExplodeSpot.alpha = 0;
        bigExplodeSpot.name = "bigExplodeSpot" + this.name;

        bigExplodeSpot.anchor.set(0.5);
        bigExplodeSpot.scale.set(scale);

        this.addChild(bigExplodeSpot);
        tl.to(bigExplodeSpot, {
            duration: 0.01,
            alpha: 1
        });
        tl.add(<gsap.core.Timeline><unknown>bigExplodeSpot.getAnimationAutoShowHide(true, false));
        tl.add(() => {
            bigExplodeSpot?.parent?.removeChild(bigExplodeSpot);
        });
        return tl;
    }

    public remove(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // #465
        if (this.name.includes("MX")) {
            return tl;
        }

        if (this.animation) {
            tl.to(this.animation.scale, {
                duration: 0.5,
                x: 0,
                y: 0
            });
        } else {
            tl.to(this._symbolSprite.scale, {
                duration: 0.5,
                x: 0,
                y: 0
            });
        }

        tl.add(() => {
            if (this.parent) {
                this.parent.removeChild(this);
                if (this.animation) {
                    this.animation.scale.set(1);
                } else {
                    this._symbolSprite.scale.set(1);
                }
            }
        });

        return tl;
    }

    public playMXAnimations(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        this.createMXSpineObj();

        if (!Game.hasGridExpanded) {
            tl.add([Game.createTimelineForAnimation(this._mXAnimation, 0, "land"),
                () => {
                    Game.soundPlayer.playSound(Sound.BANANA_LAND, 1);
                }
            ]);
            Game.hasGridExpanded = true;
        } else {
            tl.add(Game.createTimelineForAnimation(this._mXAnimation, 0, "cap_win"));
        }

        return tl;
    }

    public getGrenadeTargetRange(): string {
        const {x: targetX, y: targetY}: IMonsterWalk = Game.model.gameData.grenadePosition;
        let monsterX: number = Game.model.data.carriedSymbolUserNode.x,
            monsterY: number = Game.model.data.carriedSymbolUserNode.y;

        let distance: number = Math.max(Math.abs(targetX - monsterX), Math.abs(targetY - monsterY));
        let range: string = "";

        if (distance <= 1) { // range is close when monster self hurted
            range = "close";
        } else if (distance <= 4) {
            range = ""; // mid
        } else {
            range = "far";
        }

        return range;
    }

    private createMXSpineObj(): void {
        if (!this._mXAnimation) {
            this._mXAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("mX"));
            this._mXAnimation.name = "mXAnimation";
            this.addChild(this._mXAnimation);
        }
    }

    private createHighlightItem(): void {
        if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(this.name)) {
            this._highlight = new PIXI.Sprite(Game.assetLoader.getTextures(GameAssets.highlightPaySymbolMap.get(this.name) || '')[0]);
        } else {
            this._highlight = new PIXI.Sprite(Game.assetLoader.getTextures(GameAssets.getSymbolStateTexturePath(this.name.includes("MX") || this.name.includes("L") ? "P4" : this.name))[0]);
        }

        this._highlight.visible = false;
        this._highlight.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._highlight.anchor.set(0.5, 0.5);
    }

    private setAnimationOrSprite(): void {
        if (this.animation) {
            this.removeChild(this.animation);
        }
        if (this._symbolSprite) {
            this.removeChild(this._symbolSprite);
        }

        if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(this.name)) {
            this.animation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("allPSymbols"));
            this.addChild(this.animation);

            this.changeSymbolSkin(this.name);
        } else if (this.name === "XS" || this.name === "W" || this.name === "S" || this.name === "XB" || this.name === "XN" || Game.MONSTER_SYMBOLS.includes(this.name) || this.name === "GE") {
            this.animation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset(this.name));
            this.addChild(this.animation);

            if (this.name === "W") {
                const multiplierSkinLevel: string = Game.multiplier.getSkinLevel();
                // update to 2 wilds if symbol is already split
                if (this._isSplit) {
                    this.getStateAnimation(WildStates.SPLIT_BASE);
                }

                this.changeSymbolSkin("rainbow" + multiplierSkinLevel);
            }
        } else if (!this.name.includes("MX") && this.name.charAt(0) === "M") {
            this.animation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("multiplier"));
            this.changeSymbolSkin(this.getMultiplierSkinLevel());
            this.getStateAnimation(SymbolStates.NORMAL, 0);
            this.addChild(this.animation);
        } else {
            if (this.name === "L") { // #259
                this._symbolSprite = new PIXI.Sprite(Game.assetLoader.getTextures(GameAssets.getSymbolStateTexturePath("P4"))[0]);
                this._symbolSprite.anchor.set(0.5, 0.5);
                this.addChild(this._symbolSprite);
            } else if (this.name.indexOf("MX") > -1) {
                this.createMXSpineObj();

                // #27
                if (this.name === "MX_V_L") {
                    this._mXAnimation.position.set(Game.DEFAULT_SYMBOL_WIDTH, -Game.DEFAULT_SYMBOL_WIDTH);
                } else if (this.name === "MX_V_R") {
                    this._mXAnimation.position.set(-Game.DEFAULT_SYMBOL_WIDTH, -Game.DEFAULT_SYMBOL_WIDTH);
                }
            } else {
                this._symbolSprite = new PIXI.Sprite(Game.assetLoader.getTextures(GameAssets.getSymbolStateTexturePath(this.name))[0]);
                this._symbolSprite.anchor.set(0.5);
                this.addChild(this._symbolSprite);
            }
        }
    }

    get isDark(): boolean {
        return this._isDark;
    }

    set isDark(value: boolean) {
        this._isDark = value;
    }

    get isAnimatingWithoutTl(): boolean {
        return this._isAnimatingWithoutTl;
    }

    get isSplit(): boolean {
        return this._isSplit;
    }

    get wildSkinName(): string {
        return this._wildSkinName;
    }

    get isPayoutSymbol(): boolean {
        return this._isPayoutSymbol;
    }

    get isRemoved(): boolean {
        return this._isRemoved;
    }

    set isRemoved(value: boolean) {
        this._isRemoved = value;
    }

    get reelPosition(): number {
        return this._reelPosition;
    }

    set reelPosition(value: number) {
        this._reelPosition = value;
    }
}